import { Grid, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import { useEffect, useState } from 'react';

import moment from 'moment-timezone';

import { uploadControlQualityFiveWData } from 'common/api/index';
import { ReactComponent as Comment } from 'common/assets/images/comment.svg';
import FileUploadWithLargeDropArea from 'common/components/FileUploadWithLargeDropArea/FileUploadWithLargeDropArea';
import ColumnTwoLayoutWithDivider from 'common/components/layouts/ColumnTwoLayoutWithDivider/ColumnTwoLayoutWithDivider';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import {
	AI_SIMULATION_COPYPASTE_WORDLIMIT,
	AI_SIMULATION_TYPES,
	CONTROL_QUALITY_GRAMMAR_CHECK_COMP,
	COPYANDPASTE_TEXT,
	PLAIN_ENGLISH_SUMMARY_COLUMN_MAPPING_DATA,
	PLAIN_ENGLISH_SUMMARY_TYPE,
	REGULATION_SUMMARY_COLUMN_MAPPING_DATA,
	REGULATION_SUMMARY_TYPE,
} from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	setBulkUploadFailedStatus,
	setBulkUploadSuccessStatus,
	setControlQualityRetry,
} from 'common/store/reducer/commonReducer';
import { setRegulationText } from 'common/store/reducer/simulationReducer';
import { urlWithTenant, removeHTMLTags } from 'common/utils/utilFunctions';

import BulkUploadAlertMessage from '../Components/BulkUploadModal/BulkUploadAlertMessage';
import BulkUploadModal, {
	IFinalMappedFileInfo,
} from '../Components/BulkUploadModal/BulkUploadModal';
import {
	ButtonComp,
	useDispatch,
	useSelector,
} from '../ObligationAndControl/ObligationAndControlModules';

import {
	useNavigate,
	rrcpDashboardRoutes,
	RootState,
} from './SimulationArenaModules';

import styles from './CopyAndPateTab.module.scss';

const {
	simulationArena,
	regulationSummary,
	obligationToPolicy,
	plainEnglishSummary,
	obligationAndControl,
} = rrcpDashboardRoutes;

const { TWELVE_THOUSAND } = AI_SIMULATION_COPYPASTE_WORDLIMIT;
const { HEADING1, HEADING2 } = COPYANDPASTE_TEXT;
const {
	FILE_UPLOAD_DROP_AREA_TITLE,
	FILE_UPLOAD_DROP_AREA_SUBTITLE,
	DRAG_AREA_TITLE,
	ACCEPTED_FILE_TYPE,
	MAX_FILE_SIZE,
	MAX_FILE_LIMIT,
} = CONTROL_QUALITY_GRAMMAR_CHECK_COMP;
const { PLAIN_ENGLISH_SUMMARY, REGULATION_SUMMARY } = AI_SIMULATION_TYPES;
const { OK } = HTTP_STATUS_CODES;
const CopyAndPasteTab = ({ initialFiles = [] }: { initialFiles?: File[] }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const userTimeZone = moment.tz.guess();

	const [uploadedFiles, setUploadedFiles] = useState<File[]>(initialFiles);
	const [columnMappingEnabled, setColumnMappingEnabled] = useState(false);
	const { commentIconColor } = styles;
	const openModal = uploadedFiles.length > 0;
	const textFromFile = useSelector(
		(state: RootState) => state.simulationArena.textFromFile,
	);

	const regulationText = useSelector(
		(state: RootState) => state.simulationArena.regulationText,
	);

	const selectedAIRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedRecipe,
	);
	const [textareaData, setTextareaData] = useState<string>(regulationText);
	const [retry, setRetry] = useState<any>();
	const [uploadSuccessAlert, setUploadSuccessAlert] = useState<boolean>(false);
	const [uploadRetryAlert, setUploadRetryAlert] = useState<boolean>(false);
	const isRetryEnabled = useSelector(
		(state: RootState) => state?.common?.isControlQualityRetry,
	);
	useEffect(() => {
		if (textFromFile) {
			const truncatedText =
				textFromFile.length > TWELVE_THOUSAND
					? textFromFile.slice(0, TWELVE_THOUSAND)
					: textFromFile;
			setTextareaData(truncatedText);
		}
	}, [textFromFile]);

	const onChangeTextareaCommentsData = (event: any) => {
		setTextareaData(event?.target?.value);
	};

	const showLeftSideColumn = () => (
		<Grid
			container
			className={
				columnMappingEnabled
					? styles['section-container']
					: styles['copy-paste']
			}
			data-testid="copy-paste"
		>
			<Stack direction="column">
				<Stack direction="row" alignItems="center" spacing={1}>
					<Comment fill={commentIconColor} className={styles['copy-icon']} />
					<Typography className={styles['copy-paste-heading1']}>
						{HEADING1}
					</Typography>
				</Stack>
				<Typography className={styles['copy-paste-heading2']}>
					{HEADING2}
				</Typography>
			</Stack>
			<Grid
				item
				xs={12}
				lg={12}
				sm={12}
				md={12}
				position="relative"
				className={styles['copy-text-field']}
			>
				<TextFieldWithLabel
					label="Paste regulation Information here"
					id="task-description"
					inputProps={{
						'data-testid': 'task-description',
					}}
					placeholder="Paste here"
					value={textareaData}
					onChange={onChangeTextareaCommentsData}
					required
					multiLine
					error={false}
					helperText=""
					positionClass={
						columnMappingEnabled
							? styles['word-limit-mapping-position']
							: styles['word-limit-position']
					}
					wordLimit={TWELVE_THOUSAND}
					className={styles['copy-field-width']}
					rows={columnMappingEnabled ? 10 : 5}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				lg={12}
				sm={12}
				md={12}
				container
				justifyContent="flex-end"
				className={styles['next-btn']}
			>
				<ButtonComp
					btnType="primary"
					data-testid="next-button"
					disabled={textareaData.length === 0}
					onClick={onNextButtonClickHandler}
				>
					Next
				</ButtonComp>
			</Grid>
		</Grid>
	);

	const onNextButtonClickHandler = () => {
		dispatch(setRegulationText(removeHTMLTags(textareaData)));
		switch (selectedAIRecipe) {
			case 'Regulation summary': {
				navigate(urlWithTenant(`/${simulationArena}/${regulationSummary}`));
				break;
			}
			case 'Obligation to policy': {
				navigate(urlWithTenant(`/${simulationArena}/${obligationToPolicy}`));
				break;
			}
			case 'Plain english summary': {
				navigate(urlWithTenant(`/${simulationArena}/${plainEnglishSummary}`));
				break;
			}
			default: {
				navigate(urlWithTenant(`/${simulationArena}/${obligationAndControl}`));
			}
		}
	};

	const checkForSelectedTab = () => {
		if (
			selectedAIRecipe === PLAIN_ENGLISH_SUMMARY ||
			selectedAIRecipe === REGULATION_SUMMARY
		) {
			setColumnMappingEnabled(true);
		} else {
			setColumnMappingEnabled(false);
		}
	};
	useEffect(() => {
		checkForSelectedTab();
	}, [selectedAIRecipe]);
	const resetUploadStatusData = () => {
		dispatch(setControlQualityRetry(false));
		dispatch(setBulkUploadSuccessStatus(false));
		dispatch(setBulkUploadFailedStatus(false));
	};

	const onDocUpload = (values: any) => {
		callApi(values);
	};

	const onClose = () => {
		setUploadedFiles([]);
		resetUploadStatusData();
	};

	const callApi = async (data: IFinalMappedFileInfo[]) => {
		const formData = new FormData();
		const updatedFiles = data;

		updatedFiles?.forEach(async (item: any) => {
			const filesInfo = item?.fileInfo;
			formData.append('file', filesInfo);
			const mapInfo = Object.fromEntries(item?.mappingInfo);
			const response: any = await uploadControlQualityFiveWData(
				mapInfo,
				formData,
				uploadedFiles,
				selectedAIRecipe === PLAIN_ENGLISH_SUMMARY
					? PLAIN_ENGLISH_SUMMARY_TYPE
					: REGULATION_SUMMARY_TYPE,
				userTimeZone,
			);
			if (response?.statusCode === OK) {
				resetToInitialState();
			} else {
				handleError();
			}
		});
	};

	const resetToInitialState = () => {
		setUploadedFiles([]);
		setUploadSuccessAlert(true);
		setRetry(false);
		dispatch(setControlQualityRetry(false));
		dispatch(setBulkUploadSuccessStatus(true));
		dispatch(setBulkUploadFailedStatus(false));
	};
	const handleError = () => {
		dispatch(setBulkUploadFailedStatus(true));
		dispatch(setBulkUploadSuccessStatus(false));
		if (retry) {
			setUploadedFiles([]);
			// show toast message
			// dispatch(setControlQualityRetry(true));
		}
		if (isRetryEnabled) {
			setRetry(true);
		}
	};
	const onRetryAction = (values: any) => {
		if (!retry) {
			callApi(values);
			dispatch(setControlQualityRetry(true));
		} else {
			setUploadRetryAlert(true);
			setUploadSuccessAlert(false);
			setUploadedFiles([]);
		}
	};
	const showNotification = () => {
		let showNotification = false;
		if (uploadSuccessAlert || uploadRetryAlert) {
			showNotification = true;
		}
		return showNotification;
	};

	const onToastClose = () => {
		setUploadSuccessAlert(false);
		setUploadRetryAlert(false);
	};
	useEffect(() => {
		if (!isRetryEnabled) {
			setRetry(false);
		}
	}, [isRetryEnabled]);

	useEffect(() => {
		if (uploadedFiles?.length === 0) {
			resetUploadStatusData();
		}
	}, [uploadedFiles]);
	return (
		<>
			{columnMappingEnabled ? (
				<Grid container>
					{showNotification() && (
						<BulkUploadAlertMessage
							uploadSuccessAlert
							uploadRetryAlert
							onClose={onToastClose}
						/>
					)}
					<ColumnTwoLayoutWithDivider
						leftComponent={<div>{showLeftSideColumn()}</div>}
						rightComponent={
							<FileUploadWithLargeDropArea
								title={FILE_UPLOAD_DROP_AREA_TITLE}
								subTitle={FILE_UPLOAD_DROP_AREA_SUBTITLE}
								dragAreaTitle={DRAG_AREA_TITLE(MAX_FILE_SIZE)}
								uploadedFiles={uploadedFiles}
								setUploadedFiles={setUploadedFiles}
								accept={ACCEPTED_FILE_TYPE}
								maxfileSize={MAX_FILE_SIZE}
								maxFilesLimit={MAX_FILE_LIMIT}
							/>
						}
					/>
				</Grid>
			) : (
				<div>{showLeftSideColumn()}</div>
			)}

			{openModal && uploadedFiles && (
				<BulkUploadModal
					filesData={uploadedFiles}
					modalHeading={FILE_UPLOAD_DROP_AREA_TITLE}
					openModal={openModal}
					handleClose={onClose}
					handleSubmit={onDocUpload}
					setUploadedFiles={setUploadedFiles}
					handleRetry={onRetryAction}
					columnsData={
						selectedAIRecipe === PLAIN_ENGLISH_SUMMARY
							? PLAIN_ENGLISH_SUMMARY_COLUMN_MAPPING_DATA
							: REGULATION_SUMMARY_COLUMN_MAPPING_DATA
					}
				/>
			)}
		</>
	);
};

export default CopyAndPasteTab;
